import {useEffect, useMemo} from 'react'
import type {ReactNode} from 'react';
import moment from "moment";
import type {Moment, MomentInput} from "moment";
import FromToDatePicker from "./location/profile/FromToDatePicker";
import AvailabilityFromToDatePicker from "./location/profile/AvailabilityFromToDatePicker";
import type {AvailabilityByDate} from "./location/profile/AvailabilityPickersDay";
import {Collapse, Grid} from "@mui/material";
import {Alert} from "@mui/material";

interface CollapsibleMonthlyAutoRenewalAlertProps {
  showEndDate: boolean;
}

export const CollapsibleMonthlyAutoRenewalAlert = ({showEndDate}: CollapsibleMonthlyAutoRenewalAlertProps) => {
  return <Collapse in={!showEndDate} className={"w-100"}>
    <Alert severity={"info"}>
      This booking will renew automatically every month until cancelled.
    </Alert>
  </Collapse>;
};

interface BookingFromToDatePickerProps {
  fromDate: MomentInput;
  onFromDateChange: (date: Moment) => void;
  toDate: MomentInput;
  onToDateChange: (date: Moment) => void;
  disablePast?: boolean;
  showEndDate?: boolean;
  prePendedIcon?: ReactNode;
  postPendedIcon?: ReactNode;
  disabled?: boolean;
  disableBookingChanges?: boolean;
  hideAutoRenewalNotice?: boolean;
  availability?: AvailabilityByDate | null;
  onCalendarMonthChange?: (month: Moment) => void;
}

const BookingFromToDatePicker = ({
                                   fromDate,
                                   onFromDateChange,
                                   toDate,
                                   onToDateChange,
                                   disablePast = false,
                                   showEndDate = true,
                                   prePendedIcon,
                                   postPendedIcon,
                                   disabled = false,
                                   disableBookingChanges,
                                   hideAutoRenewalNotice = false,
                                   availability,
                                   onCalendarMonthChange,
                                 }: BookingFromToDatePickerProps) => {

  const minToDate = useMemo(() => {
    return moment(fromDate, "MM/DD/YYYY").add(1, 'days');
  }, [fromDate]);

  // The selected months' availability decides whether the availability calendar is shown at all.
  const fromMonth = moment(fromDate, "MM/DD/YYYY").format('YYYY-MM');
  const toMonth = showEndDate ? moment(toDate, "MM/DD/YYYY").format('YYYY-MM') : null;
  useEffect(() => {
    if (!onCalendarMonthChange) return;
    [fromMonth, toMonth].forEach((month) => {
      if (month && moment(month, 'YYYY-MM', true).isValid()) onCalendarMonthChange(moment(month, 'YYYY-MM'));
    });
  }, [fromMonth, toMonth, onCalendarMonthChange]);

  const handleFromDateChange = (date: Moment | null) => {
    if (moment(date).isSameOrAfter(toDate, 'day')) {
      onToDateChange(moment(date).add(1, 'days'));
    }
    onFromDateChange(moment(date, "MM/DD/YYYY"));
  };

  const handleToDateChange = (date: Moment | null) => {
    if (moment(date).isSameOrBefore(fromDate, 'day')) {
      onToDateChange(moment(minToDate, "MM/DD/YYYY"));
    } else {
      onToDateChange(moment(date, "MM/DD/YYYY"));
    }
  };

  // Restores dates as they were, without the adjustments above (e.g. To moved past a picked From).
  const restoreDates = (from: Moment, to: Moment) => {
    onFromDateChange(from);
    onToDateChange(to);
  };

  return (
    <Grid item container alignContent={'center'}>
      {prePendedIcon ? <Grid item className={'ss-byb-details-icon'}>{prePendedIcon}</Grid> : ''}
      {availability ?
        <AvailabilityFromToDatePicker
          fromDate={fromDate}
          setToDate={handleToDateChange}
          setFromDate={handleFromDateChange}
          restoreDates={restoreDates}
          toDate={toDate}
          disabled={disabled}
          disablePast={disablePast}
          showEndDate={showEndDate}
          minToDate={minToDate}
          availability={availability}
          onCalendarMonthChange={onCalendarMonthChange}
        /> :
        <FromToDatePicker
          fromDate={fromDate}
          setToDate={handleToDateChange}
          setFromDate={handleFromDateChange}
          toDate={toDate}
          disabled={disabled}
          disablePast={disablePast}
          showEndDate={showEndDate}
          minToDate={minToDate}
        />
      }
      {postPendedIcon && !disableBookingChanges ?
        <Grid item className={'ss-byb-details-icon'}>{postPendedIcon}</Grid> : ''}
      {!hideAutoRenewalNotice && <Grid item xs={12}>
        <CollapsibleMonthlyAutoRenewalAlert showEndDate={showEndDate}/>
      </Grid>
      }
    </Grid>
  );
};

export default BookingFromToDatePicker;
